import * as signalR from '@microsoft/signalr'
import { LogLevel } from '@microsoft/signalr'
import { AxiosInstance, AxiosResponse } from 'axios'
import { Dispatch, Store } from 'redux'

import { LogActions } from '../redux/logs/actions'
import { getToken } from '../redux/user/selectors'

import { mapActionError, wait } from './helpers'

async function startConnection(dispatch: any, connection: signalR.HubConnection, reconnectTimeoutCounter = 0) {
  const realtimeReconnectTimeouts = [1000, 5000, 10000, 20000, 30000]
  try {
    await connection.start()
  } catch (e) {
    if (reconnectTimeoutCounter > realtimeReconnectTimeouts.length) {
      dispatch(LogActions.addLogItem(mapActionError(e, 'ERROR')))
    } else {
      await wait(realtimeReconnectTimeouts[reconnectTimeoutCounter])
      await startConnection(dispatch, connection, reconnectTimeoutCounter + 1)
    }
  }
}

export class SignalR {
  private static connection: signalR.HubConnection

  private static store: Store

  static setStore(store: Store) {
    SignalR.store = store
  }

  static async start(axios: AxiosInstance) {
    const { dispatch } = SignalR.store
    SignalR.stop()
    let tokenResponse: AxiosResponse<{ token: string }> | undefined
    try {
      tokenResponse = await axios.post<{ token: string }>('/api/notification/token')
    } catch (e) {
      dispatch(LogActions.addLogItem(mapActionError(e, 'ERROR')))
      return
    }

    const getXsrfToken = () =>
      document.cookie
        ?.split('; ')
        ?.find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1]

    const generateXsrfTokenHeader = (): signalR.MessageHeaders => {
      const xsrfToken = getXsrfToken()
      return xsrfToken ? { 'X-XSRF-TOKEN': xsrfToken } : {}
    }

    SignalR.connection = new signalR.HubConnectionBuilder()
      .configureLogging(process.env.NODE_ENV === 'development' ? LogLevel.Debug : LogLevel.Error)
      .withUrl(
        `${axios.defaults.baseURL || ''}/api/notification/hubs/events?token=${encodeURIComponent(
          tokenResponse.data.token
        )}`,
        {
          withCredentials: false,
          headers: { Authorization: `Bearer ${getToken(SignalR.store.getState())}`, ...generateXsrfTokenHeader() }
        }
      )
      .build()
    SignalR.connection.onclose(async err => {
      if (err) await startConnection(dispatch, SignalR.connection)
    })

    await startConnection(dispatch, SignalR.connection)
  }

  static addSubscriptionHandler(
    subscriptionString: string,
    createResponseHandler: (dispatch: Dispatch) => (response: any) => void
  ) {
    const { dispatch } = SignalR.store
    SignalR.connection.on(subscriptionString, createResponseHandler(dispatch))
  }

  static stop() {
    if (!SignalR.connection) return
    SignalR.connection.stop()
  }

  static isConnected(): boolean {
    if (!SignalR.connection) return false
    return SignalR.connection.state === signalR.HubConnectionState.Connected
  }
}
